import i18next, { type i18n as I18n, type TOptions, type StringMap } from 'i18next';
import { BUILD_LOCALE } from '@/ots-constants';
import { defaultLocale } from '@/i18n/constants';
import type { I18nKey } from '@/i18n/types';

// Server-safe i18next instance. No react-i18next binding here — that would
// pull in React.createContext at module load and break RSC server graphs.
// The client-side react integration lives in `@/i18n/clientI18n` and is
// applied lazily when the I18nextProvider mounts.
//
// Each container/build is locked to a single locale via NEXT_PUBLIC_LANG;
// resources are require()d at module load so the instance is fully populated
// synchronously on both server and client. No HTTP backend, no async init.
//
// Translation updates ride the deploy cycle. The old next-i18next setup
// polled memcache every 30 min for server-side hot reload; that path is gone.
// If we want live updates back later, add a polling client + addResourceBundle
// behind a feature flag — keep this module sync.

const NAMESPACES = ['common', 'unit', 'seo'] as const;
type Namespace = (typeof NAMESPACES)[number];

export const activeLocale = BUILD_LOCALE || defaultLocale;

// The next.config.js ContextReplacementPlugin scopes /public/locales to
// `en` + the active locale. Webpack inlines these requires into the bundle.
function loadResources() {
  const resources: Record<string, Partial<Record<Namespace, object>>> = {};
  const loadLocale = (lng: string) => {
    resources[lng] = {};
    NAMESPACES.forEach((ns) => {
      try {
        // eslint-disable-next-line global-require, import/no-dynamic-require
        resources[lng][ns] = require(`../../public/locales/${lng}/${ns}.json`);
      } catch {
        // Some namespaces don't exist for every locale (e.g. en/seo.json).
        // Missing bundles fall through to the `en` default-locale chain.
        resources[lng][ns] = {};
      }
    });
  };

  loadLocale('en');
  if (activeLocale !== 'en') loadLocale(activeLocale);
  return resources;
}

let instance: I18n | null = null;

export function getI18nInstance(): I18n {
  if (instance) return instance;

  const i = i18next.createInstance();
  i.init({
    lng: activeLocale,
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: NAMESPACES as unknown as string[],
    resources: loadResources(),
    interpolation: {
      skipOnVariables: false,
      escapeValue: false,
    },
    initImmediate: false,
  }).catch(() => {});
  instance = i;
  return i;
}

export const i18n = getI18nInstance();

// For server components: bound `t` function for a given namespace. Mirrors
// the client-side `useOTSTranslation` shape (returns strictly `string`) so
// consumers can swap between the two without changing call sites.
export function getServerT<NS extends Namespace>(
  ns: NS,
): (key: I18nKey[NS], options?: TOptions<StringMap> | string) => string {
  const t = i18n.getFixedT(activeLocale, ns);
  return (key, options) => t(key as string, options) as string;
}
